/**
 * Task filter interface for type-safe query keys
 * 
 * @property topicId - Filter by topic ID
 * @property status - Filter by task status
 * @property assignedTo - Filter by assigned user ID
 * @property search - Filter by search term
 * @property tags - Filter by tags
 * @property isHighlighted - Filter by highlighted status
 * @property deadline - Filter by deadline
 * @property sortBy - Field to sort by
 * @property sortDirection - Direction to sort (asc/desc)
 */
export interface TaskFilters {
  topicId?: string;
  status?: string;
  assignedTo?: string;
  search?: string;
  tags?: string[];
  isHighlighted?: boolean;
  deadline?: {
    before?: string;
    after?: string;
    exists?: boolean;
  };
  sortBy?: 'position' | 'title' | 'created' | 'updated' | 'deadline';
  sortDirection?: 'asc' | 'desc';
}

/**
 * Query key factory for tasks
 * 
 * These keys are used to identify and organize React Query cache entries
 * related to tasks. Following TanStack Query best practices for structured keys.
 * 
 * Query keys are organized hierarchically to allow for selective invalidation:
 * - taskKeys.all: Invalidates all task-related queries
 * - taskKeys.lists(): Invalidates all task list queries
 * - taskKeys.list(filters): Invalidates specific filtered lists
 * - taskKeys.byTopic(topicId): Invalidates tasks for a specific topic
 * - taskKeys.byStatus(topicId, status): Invalidates tasks with a specific status in a topic
 * - taskKeys.details(): Invalidates all individual task queries
 * - taskKeys.detail(id): Invalidates a specific task
 * 
 * Usage examples:
 * - taskKeys.all - Base key for all task data
 * - taskKeys.lists() - Key for all task lists
 * - taskKeys.list({ topicId: 'topic-123' }) - Key for tasks in a specific topic
 * - taskKeys.detail('task-123') - Key for a specific task
 * 
 * @example
 * // Invalidate all task data
 * queryClient.invalidateQueries({ queryKey: taskKeys.all });
 * 
 * @example
 * // Fetch tasks for a specific topic
 * useQuery({ queryKey: taskKeys.byTopic('topic-123'), queryFn: fetchTasks });
 */
export const taskKeys = {
  /**
   * Base key for all task-related queries
   * Invalidating this key will invalidate all task queries
   */
  all: ['tasks'] as const,
  
  /**
   * Keys for all task lists
   * Invalidating this key will invalidate all list queries but not detail queries
   * 
   * @returns Tuple of ['tasks', 'list']
   */
  lists: () => [...taskKeys.all, 'list'] as const,
  
  /**
   * Key for filtered task lists
   * Used for storing and retrieving specific filtered views of tasks
   * 
   * @param filters - Object containing filter criteria
   * @returns Tuple of ['tasks', 'list', {...filters}]
   * 
   * @example
   * // Get tasks for a specific topic
   * taskKeys.list({ topicId: 'topic-123' })
   * 
   * @example
   * // Get tasks with a specific status
   * taskKeys.list({ topicId: 'topic-123', status: 'doing' })
   * 
   * @example
   * // Get tasks assigned to a specific user
   * taskKeys.list({ topicId: 'topic-123', assignedTo: 'user-123' })
   * 
   * @example
   * // Get sorted tasks
   * taskKeys.list({ topicId: 'topic-123', sortBy: 'deadline', sortDirection: 'asc' })
   */
  list: (filters: TaskFilters = {}) => [...taskKeys.lists(), filters] as const,
  
  /**
   * Keys for all individual task details
   * Invalidating this will invalidate all individual task queries
   * 
   * @returns Tuple of ['tasks', 'detail']
   */
  details: () => [...taskKeys.all, 'detail'] as const,
  
  /**
   * Key for a specific task by ID
   * Used for storing and retrieving data about a specific task
   * 
   * @param id - Task ID
   * @returns Tuple of ['tasks', 'detail', id]
   * 
   * @example
   * // Get task with ID "task-123"
   * taskKeys.detail('task-123')
   * 
   * @example
   * // Invalidate a specific task
   * queryClient.invalidateQueries({ queryKey: taskKeys.detail('task-123') })
   */
  detail: (id: string) => [...taskKeys.details(), id] as const,
  
  /**
   * Key for all tasks in a specific topic
   * Useful for invalidating all tasks when a topic changes
   * 
   * @param topicId - Topic ID
   * @returns Tuple of ['tasks', 'by-topic', topicId]
   * 
   * @example
   * // Invalidate all tasks in a topic
   * queryClient.invalidateQueries({ queryKey: taskKeys.byTopic('topic-123') })
   */
  byTopic: (topicId: string) => [...taskKeys.all, 'by-topic', topicId] as const,

  /**
   * Key for tasks with a specific status in a topic
   * Useful for invalidating tasks in a specific column
   * 
   * @param topicId - Topic ID
   * @param status - Task status
   * @returns Tuple of ['tasks', 'by-topic', topicId, 'status', status]
   * 
   * @example
   * // Invalidate all "doing" tasks in a topic
   * queryClient.invalidateQueries({ queryKey: taskKeys.byStatus('topic-123', 'doing') })
   */
  byStatus: (topicId: string, status: string) => 
    [...taskKeys.byTopic(topicId), 'status', status] as const,
    
  /**
   * Key for tasks assigned to a specific user
   * Useful for invalidating tasks when user assignments change
   * 
   * @param userId - User ID
   * @returns Tuple of ['tasks', 'by-assignee', userId]
   * 
   * @example
   * // Invalidate all tasks assigned to a user
   * queryClient.invalidateQueries({ queryKey: taskKeys.byAssignee('user-123') })
   */
  byAssignee: (userId: string) => [...taskKeys.all, 'by-assignee', userId] as const,
  
  /**
   * Key for dashboard tasks
   * Used for the dashboard view which shows tasks across projects
   * 
   * @param userId - User ID
   * @returns Tuple of ['tasks', 'dashboard', userId]
   */
  dashboard: (userId: string) => [...taskKeys.all, 'dashboard', userId] as const,
  
  /**
   * Key for storing filter preferences for a specific topic
   * 
   * @param topicId - Topic ID
   * @returns Tuple of ['tasks', 'filters', topicId]
   */
  filters: (topicId: string) => [...taskKeys.all, 'filters', topicId] as const,
};